import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Download, RefreshCw, Link as LinkIcon, CheckCircle, Clock3, AlertCircle, ExternalLink, FileDown, Trash2, ChevronLeft, ChevronRight, Search } from "lucide-react";
import AdminLayout, { AdminPageHead } from "./AdminLayout";
import { fetchOutreachCandidates, ingestOutreachCandidates, refreshOutreachLinks, downloadOutreachCsv, deleteOutreachCandidate, getOutreachIngestStatus } from "../service/OutreachService";
import { getJwtRole } from "../service/AuthService";
import { admin } from "../constants/Constants";

function pageWindows(current, total) {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const show = new Set([1, total, current - 1, current, current + 1].filter(n => n >= 1 && n <= total));
    const sorted = [...show].sort((a, b) => a - b);
    const result = [];
    for (let i = 0; i < sorted.length; i++) {
        if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push(null);
        result.push(sorted[i]);
    }
    return result;
}

function fmt(ts) {
    if (!ts) return "—";
    return new Date(ts).toLocaleString("en-US", {
        month: "short", day: "numeric", year: "numeric",
        hour: "numeric", minute: "2-digit", hour12: true,
    });
}

// YYYY-MM-DD for a date `daysAgo` days before today (local time).
function dateStr(daysAgo = 0) {
    const d = new Date();
    d.setDate(d.getDate() - daysAgo);
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${d.getFullYear()}-${m}-${day}`;
}

function statusBadge(status) {
    const s = (status || "").toUpperCase();
    if (s === "READY") {
        return <span className="status-badge badge-complete"><CheckCircle size={12} />Ready</span>;
    }
    if (s === "SCORED") {
        return <span className="status-badge badge-complete"><CheckCircle size={12} />Scored</span>;
    }
    if (s === "SUBMITTED" || s === "INGESTED") {
        return <span className="status-badge badge-processing"><Clock3 size={12} />{s === "SUBMITTED" ? "Scoring" : "Ingested"}</span>;
    }
    if (s === "FAILED") {
        return <span className="status-badge badge-error"><AlertCircle size={12} />Failed</span>;
    }
    return <span className="status-badge">{status || "—"}</span>;
}

export default function AdminOutreach() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [candidates, setCandidates] = useState([]);
    const [ingesting, setIngesting] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [message, setMessage] = useState("");
    const [copiedId, setCopiedId] = useState(null);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState("");
    const [showRejected, setShowRejected] = useState(false);
    const PAGE_SIZE = 10;

    // Scrape modal state
    const [scrapeModal, setScrapeModal] = useState(false);
    const [scrapeStart, setScrapeStart] = useState(dateStr(30));
    const [scrapeEnd, setScrapeEnd] = useState(dateStr(0));

    // Export modal state
    const [exportModal, setExportModal] = useState(false);
    const [exportStart, setExportStart] = useState("");
    const [exportEnd, setExportEnd] = useState("");
    // Which date the export range filters on: "posted" (paper's posted date) or
    // "ingested" (when the candidate was added here).
    const [exportDateField, setExportDateField] = useState("posted");
    // Optional sixth CSV column with the paper title; off by default so the
    // export keeps the plain Apollo import shape unless asked for.
    const [exportIncludeTitle, setExportIncludeTitle] = useState(false);

    // Span in whole days between the two scrape dates (NaN if either is blank).
    const scrapeSpanDays = (scrapeStart && scrapeEnd)
        ? (new Date(scrapeEnd).getTime() - new Date(scrapeStart).getTime()) / 86400000
        : NaN;
    const scrapeStartAfterEnd = !Number.isNaN(scrapeSpanDays) && scrapeSpanDays < 0;
    const scrapeRangeTooLong = !Number.isNaN(scrapeSpanDays) && scrapeSpanDays > 31;
    const scrapeValid = !!scrapeStart && !!scrapeEnd && !scrapeStartAfterEnd && !scrapeRangeTooLong;

    useEffect(() => { setPage(1); }, [search, showRejected]);

    const isFailed = (c) => ((c?.effectiveStatus ?? c?.status ?? "") + "").toUpperCase() === "FAILED";
    const rejectedCount = candidates.filter(isFailed).length;

    useEffect(() => {
        if (getJwtRole() !== admin) {
            navigate("/");
            return;
        }
        load();
        // Reflect a job that may already be running (started by another tab or
        // before a page refresh) so the button stays disabled until it finishes.
        getOutreachIngestStatus().then(s => {
            if (s?.running) setIngesting(true);
        }).catch(() => {});
    }, []);

    // While an ingest is running, poll status every 5s. When it flips to
    // not-running, refresh the candidate list and clear the ingesting flag.
    useEffect(() => {
        if (!ingesting) return;
        const id = setInterval(async () => {
            try {
                const s = await getOutreachIngestStatus();
                if (s?.running === false) {
                    clearInterval(id);
                    setIngesting(false);
                    setMessage("Ingest finished. Updated the candidates list below.");
                    load();
                }
            } catch {}
        }, 5000);
        return () => clearInterval(id);
    }, [ingesting]);

    const load = () => {
        setLoading(true);
        fetchOutreachCandidates()
            .then(data => setCandidates(data.candidates ?? []))
            .catch(() => {})
            .finally(() => setLoading(false));
    };

    const openScrapeModal = () => {
        setScrapeStart(dateStr(30));
        setScrapeEnd(dateStr(0));
        setScrapeModal(true);
    };

    const handleIngest = async () => {
        if (!scrapeValid) return;
        setMessage("");
        try {
            const res = await ingestOutreachCandidates(scrapeStart, scrapeEnd);
            if (res?.success) {
                setScrapeModal(false);
                setIngesting(true);
                setMessage("Ingest started. Pulling candidates from bioRxiv in the background — this typically takes a couple of minutes. The list will refresh automatically when it finishes.");
            } else if (res?.error === "ALREADY_RUNNING") {
                setScrapeModal(false);
                setIngesting(true);
                setMessage("An ingest is already running. Please wait for it to finish before starting another.");
            } else if (res?.error === "INVALID_RANGE") {
                setMessage("That date range isn't valid. Please pick a start date on or before the end date, no more than one month apart.");
            } else {
                setMessage("There was an error starting the ingest. Please try again.");
            }
        } catch {
            setMessage("There was an error starting the ingest. Please try again.");
        }
    };

    const handleRefresh = async () => {
        setRefreshing(true);
        setMessage("");
        try {
            const res = await refreshOutreachLinks();
            if (res?.success) {
                const n = res.updated ?? 0;
                setMessage(`Updated ${n} link${n === 1 ? "" : "s"}.`);
                load();
            } else {
                setMessage("There was an error refreshing links. Please try again.");
            }
        } catch {
            setMessage("There was an error refreshing links. Please try again.");
        } finally {
            setRefreshing(false);
        }
    };

    const openExportModal = () => {
        setExportStart("");
        setExportEnd("");
        setExportDateField("posted");
        setExportIncludeTitle(false);
        setExportModal(true);
    };

    const handleExport = async (startDate, endDate, dateField, includeTitle) => {
        setExporting(true);
        setMessage("");
        try {
            const { blob, disposition } = await downloadOutreachCsv(startDate || "", endDate || "", dateField || "posted", !!includeTitle);
            const match = disposition && disposition.match(/filename="?([^"]+)"?/);
            const filename = match ? match[1] : "outreach_candidates.csv";
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            a.click();
            window.URL.revokeObjectURL(url);
            setExportModal(false);
        } catch {
            setMessage("There was an error exporting the CSV. Please try again.");
        } finally {
            setExporting(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this candidate? This also removes the manuscript, showcase link, and any related scoring data.")) return;
        setDeletingId(id);
        setMessage("");
        try {
            const res = await deleteOutreachCandidate(id);
            if (res?.success) {
                setCandidates(prev => prev.filter(c => c.id !== id));
            } else {
                setMessage("There was an error deleting the candidate. If scoring is still in progress, wait for it to finish and try again.");
            }
        } catch {
            setMessage("There was an error deleting the candidate. Please try again.");
        } finally {
            setDeletingId(null);
        }
    };

    const copyLink = (id, link) => {
        if (!link) return;
        navigator.clipboard.writeText(link);
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 2000);
    };

    const overlays = (
        <>
            {/* Pull Candidates (scrape) modal */}
            {scrapeModal && (
                <div className="share-modal-overlay" onClick={() => setScrapeModal(false)}>
                    <div className="share-modal-tile" onClick={e => e.stopPropagation()}>
                        <div className="share-modal-header">
                            <p className="share-modal-title">Pull Candidates</p>
                            <button className="share-modal-close" onClick={() => setScrapeModal(false)}>×</button>
                        </div>
                        <div className="share-modal-body">
                            <p style={{ margin: "0 0 14px", fontSize: 12.5, color: "var(--ink-500)", lineHeight: 1.5 }}>
                                Pull preprints posted within a date range. The range can span at most one month.
                            </p>
                            <div style={{ display: "grid", gap: 6 }}>
                                <label style={{ fontSize: 11, color: "var(--ink-400)" }}>Start date</label>
                                <input type="date" value={scrapeStart} max={scrapeEnd || undefined} onChange={e => setScrapeStart(e.target.value)} className="sub-date-input" />
                                <label style={{ fontSize: 11, color: "var(--ink-400)", marginTop: 4 }}>End date</label>
                                <input type="date" value={scrapeEnd} min={scrapeStart || undefined} onChange={e => setScrapeEnd(e.target.value)} className="sub-date-input" />
                            </div>
                            {scrapeStartAfterEnd && (
                                <p className="outreach-modal-warn">Start date must be on or before the end date.</p>
                            )}
                            {scrapeRangeTooLong && (
                                <p className="outreach-modal-warn">Date range cannot exceed 1 month.</p>
                            )}
                            <button
                                className="auth-submit-btn"
                                disabled={!scrapeValid}
                                style={{ marginTop: 16, opacity: scrapeValid ? 1 : 0.6, cursor: scrapeValid ? "pointer" : "not-allowed" }}
                                onClick={handleIngest}
                            >
                                Start Scrape
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Export CSV modal */}
            {exportModal && (
                <div className="share-modal-overlay" onClick={() => { if (!exporting) setExportModal(false); }}>
                    <div className="share-modal-tile" onClick={e => e.stopPropagation()}>
                        <div className="share-modal-header">
                            <p className="share-modal-title">Export CSV</p>
                            <button className="share-modal-close" onClick={() => { if (!exporting) setExportModal(false); }}>×</button>
                        </div>
                        <div className="share-modal-body">
                            <p style={{ margin: "0 0 14px", fontSize: 12.5, color: "var(--ink-500)", lineHeight: 1.5 }}>
                                Filter the export by a date range, or leave both dates blank to export every candidate.
                            </p>
                            <div style={{ display: "grid", gap: 6 }}>
                                <label style={{ fontSize: 11, color: "var(--ink-400)" }}>Start date <span style={{ color: "var(--ink-300)", fontWeight: 400 }}>(optional)</span></label>
                                <input type="date" value={exportStart} onChange={e => setExportStart(e.target.value)} className="sub-date-input" />
                                <label style={{ fontSize: 11, color: "var(--ink-400)", marginTop: 4 }}>End date <span style={{ color: "var(--ink-300)", fontWeight: 400 }}>(optional)</span></label>
                                <input type="date" value={exportEnd} onChange={e => setExportEnd(e.target.value)} className="sub-date-input" />
                            </div>
                            <div style={{ display: "grid", gap: 6, marginTop: 12 }}>
                                <label style={{ fontSize: 11, color: "var(--ink-400)" }}>Filter dates by</label>
                                <label style={{ fontSize: 12.5, color: "var(--ink-750)", display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                                    <input type="radio" name="exportDateField" value="posted"
                                        checked={exportDateField === "posted"}
                                        onChange={() => setExportDateField("posted")} />
                                    Date the paper was posted
                                </label>
                                <label style={{ fontSize: 12.5, color: "var(--ink-750)", display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                                    <input type="radio" name="exportDateField" value="ingested"
                                        checked={exportDateField === "ingested"}
                                        onChange={() => setExportDateField("ingested")} />
                                    Date added here (submitted/ingested)
                                </label>
                            </div>
                            <div style={{ display: "grid", gap: 6, marginTop: 12 }}>
                                <label style={{ fontSize: 11, color: "var(--ink-400)" }}>Columns</label>
                                <label style={{ fontSize: 12.5, color: "var(--ink-750)", display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                                    <input type="checkbox"
                                        checked={exportIncludeTitle}
                                        onChange={e => setExportIncludeTitle(e.target.checked)} />
                                    Include the paper title
                                </label>
                            </div>
                            <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                                <button
                                    className="sub-date-clear"
                                    style={{ flex: 1, padding: "10px", opacity: exporting ? 0.6 : 1, cursor: exporting ? "default" : "pointer" }}
                                    disabled={exporting}
                                    onClick={() => handleExport("", "", exportDateField, exportIncludeTitle)}
                                >
                                    Export all
                                </button>
                                {(() => {
                                    // The backend only filters when BOTH dates are present and
                                    // start <= end; anything else silently exports everything.
                                    // Block that here rather than handing back a surprise full dump.
                                    const partial = (!!exportStart) !== (!!exportEnd);
                                    const inverted = exportStart && exportEnd && exportStart > exportEnd;
                                    const blocked = exporting || partial || inverted;
                                    return (
                                        <button
                                            className="auth-submit-btn"
                                            style={{ flex: 1, marginTop: 0, opacity: blocked ? 0.6 : 1, cursor: blocked ? "default" : "pointer" }}
                                            disabled={blocked}
                                            title={partial ? "Set both dates, or use Export all" : (inverted ? "Start date must be on or before the end date" : "")}
                                            onClick={() => handleExport(exportStart, exportEnd, exportDateField, exportIncludeTitle)}
                                        >
                                            {exporting ? "Exporting..." : "Export"}
                                        </button>
                                    );
                                })()}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );

    return (
        <AdminLayout overlays={overlays}>
                    <AdminPageHead
                        title="Outreach Candidates"
                        subtitle="Pull microbiology / virology preprints from bioRxiv and prepare them for outreach."
                        actions={<>
                            <button
                                onClick={openScrapeModal}
                                disabled={ingesting}
                                style={{
                                    display: "flex", alignItems: "center", gap: 6,
                                    padding: "8px 16px", borderRadius: 8,
                                    border: "none", background: "var(--brand)",
                                    color: "white", cursor: ingesting ? "default" : "pointer",
                                    fontSize: 13, fontWeight: 500, opacity: ingesting ? 0.6 : 1,
                                    whiteSpace: "nowrap",
                                }}
                            >
                                <Download size={14} />
                                {ingesting ? "Pulling..." : "Pull Candidates"}
                            </button>
                            <button
                                onClick={handleRefresh}
                                disabled={refreshing}
                                style={{
                                    display: "flex", alignItems: "center", gap: 6,
                                    padding: "8px 16px", borderRadius: 8,
                                    border: "1px solid var(--border)", background: "var(--surface)",
                                    cursor: refreshing ? "default" : "pointer",
                                    fontSize: 13, fontWeight: 500, color: "var(--ink-900)",
                                    opacity: refreshing ? 0.6 : 1, whiteSpace: "nowrap",
                                }}
                            >
                                <RefreshCw size={14} />
                                {refreshing ? "Refreshing..." : "Refresh Links"}
                            </button>
                            <button
                                onClick={openExportModal}
                                disabled={exporting || candidates.length === 0}
                                style={{
                                    display: "flex", alignItems: "center", gap: 6,
                                    padding: "8px 16px", borderRadius: 8,
                                    border: "1px solid var(--border)", background: "var(--surface)",
                                    cursor: (exporting || candidates.length === 0) ? "default" : "pointer",
                                    fontSize: 13, fontWeight: 500, color: "var(--ink-900)",
                                    opacity: (exporting || candidates.length === 0) ? 0.6 : 1, whiteSpace: "nowrap",
                                }}
                            >
                                <FileDown size={14} />
                                {exporting ? "Exporting..." : "Export CSV"}
                            </button>
                        </>}
                    />

                    {message && (
                        <div style={{
                            padding: "10px 14px", borderRadius: 8,
                            background: "var(--blue-bg)", color: "var(--ink-900)",
                            fontSize: 13, marginBottom: 14,
                        }}>
                            {message}
                        </div>
                    )}

                    <div className="submissions-card">
                        <div className="admin-users-header">
                            <p style={{ margin: 0, fontSize: 14.5, fontWeight: 600, color: "var(--ink-900)", flex: 1 }}>
                                Candidates <span style={{ fontSize: 12, color: "var(--ink-400)", fontWeight: 400 }}>({candidates.length - rejectedCount}{rejectedCount > 0 ? ` · ${rejectedCount} rejected` : ""})</span>
                            </p>
                            {rejectedCount > 0 && (
                                <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--ink-500)", cursor: "pointer", userSelect: "none" }}>
                                    <input
                                        type="checkbox"
                                        checked={showRejected}
                                        onChange={e => setShowRejected(e.target.checked)}
                                        style={{ cursor: "pointer" }}
                                    />
                                    Show rejected
                                </label>
                            )}
                            <div className="submissions-search-wrap" style={{ position: "relative" }}>
                                <Search size={13} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--ink-300)", pointerEvents: "none" }} />
                                <input
                                    type="text"
                                    placeholder="Search…"
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                    style={{ paddingLeft: 30, paddingRight: search ? 26 : 10, paddingTop: 7, paddingBottom: 7, border: "1px solid var(--border)", borderRadius: 8, fontSize: 13, background: "var(--surface-2)", outline: "none", width: 200 }}
                                />
                                {search && (
                                    <button onClick={() => setSearch("")} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--ink-300)", fontSize: 16, lineHeight: 1 }}>×</button>
                                )}
                            </div>
                        </div>

                        {(() => {
                            const q = search.trim().toLowerCase();
                            const visible = showRejected ? candidates : candidates.filter(c => !isFailed(c));
                            const filtered = !q ? visible : visible.filter(c => {
                                const hay = [
                                    c.authorFirstName, c.authorLastName, c.authorFullName,
                                    c.authorEmail, c.title, c.authorInstitution, c.doi,
                                ].filter(Boolean).join(" ").toLowerCase();
                                return hay.includes(q);
                            });
                            const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
                            const safePage = Math.min(page, totalPages);
                            const paginated = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
                            return loading ? (
                            <div style={{ padding: "40px 20px", textAlign: "center", color: "var(--ink-300)", fontSize: 13 }}>Loading…</div>
                        ) : candidates.length === 0 ? (
                            <div style={{ padding: "40px 20px", textAlign: "center", color: "var(--ink-300)", fontSize: 13 }}>
                                No candidates yet. Click "Pull Candidates" to fetch from bioRxiv.
                            </div>
                        ) : filtered.length === 0 ? (
                            <div style={{ padding: "40px 20px", textAlign: "center", color: "var(--ink-300)", fontSize: 13 }}>
                                No candidates match "{search}".
                            </div>
                        ) : (
                            <>
                            {totalPages > 1 && (
                                <div style={{ padding: "12px 20px 4px" }}>
                                    <div className="pagination-bar">
                                        <p className="pagination-count">{(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, filtered.length)} of {filtered.length}</p>
                                        <div className="pagination-controls">
                                            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={safePage === 1} style={{ padding: "5px 12px", borderRadius: 7, border: "1px solid var(--border)", background: safePage === 1 ? "var(--surface-3)" : "var(--surface)", cursor: safePage === 1 ? "default" : "pointer", fontSize: 12, color: safePage === 1 ? "var(--ink-200)" : "var(--ink-700)", display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}><ChevronLeft size={13} /><span className="pagination-btn-text">Previous</span></button>
                                            <div className="pagination-pages">
                                                {pageWindows(safePage, totalPages).map((n, i) =>
                                                    n === null
                                                        ? <span key={`e${i}`} style={{ padding: "5px 2px", fontSize: 12, color: "var(--ink-300)" }}>…</span>
                                                        : <button key={n} onClick={() => setPage(n)} style={{ padding: "5px 10px", borderRadius: 7, border: "1px solid var(--border)", background: n === safePage ? "var(--brand)" : "var(--surface)", color: n === safePage ? "white" : "var(--ink-700)", cursor: "pointer", fontSize: 12, fontWeight: n === safePage ? 600 : 400 }}>{n}</button>
                                                )}
                                            </div>
                                            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={safePage === totalPages} style={{ padding: "5px 12px", borderRadius: 7, border: "1px solid var(--border)", background: safePage === totalPages ? "var(--surface-3)" : "var(--surface)", cursor: safePage === totalPages ? "default" : "pointer", fontSize: 12, color: safePage === totalPages ? "var(--ink-200)" : "var(--ink-700)", display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}><span className="pagination-btn-text">Next</span><ChevronRight size={13} /></button>
                                        </div>
                                        <div className="pagination-spacer" />
                                    </div>
                                </div>
                            )}
                            <table className="submissions-table">
                                <thead>
                                    <tr>
                                        <th>Author</th>
                                        <th className="admin-col-email">Email</th>
                                        <th>Title</th>
                                        <th>Status</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginated.map(c => (
                                        <tr key={c.id} className="submission-row">
                                            <td>
                                                <div>
                                                    <span style={{ fontWeight: 500, color: "var(--ink-900)", fontSize: 13 }}>
                                                        {c.authorFullName || `${c.authorFirstName || ""} ${c.authorLastName || ""}`.trim() || "—"}
                                                    </span>
                                                    {c.authorInstitution && (
                                                        <p style={{ margin: "2px 0 0", fontSize: 11, color: "var(--ink-400)" }}>
                                                            {c.authorInstitution}
                                                        </p>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="admin-col-email" style={{ color: "var(--ink-500)", fontSize: 13 }}>
                                                {c.authorEmail || "—"}
                                            </td>
                                            <td>
                                                <span style={{ color: "var(--ink-900)", fontSize: 13 }}>
                                                    {c.title ? (c.title.length > 80 ? c.title.slice(0, 80) + "…" : c.title) : "—"}
                                                </span>
                                                {c.postedDate && (
                                                    <p style={{ margin: "2px 0 0", fontSize: 11, color: "var(--ink-400)" }}>
                                                        {c.category} · {c.postedDate}
                                                    </p>
                                                )}
                                            </td>
                                            <td>{statusBadge(c.effectiveStatus || c.status)}</td>
                                            <td>
                                                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                                                    {c.linkToSend && (
                                                        <button
                                                            onClick={() => copyLink(c.id, c.linkToSend)}
                                                            style={{
                                                                background: "none", border: "1px solid var(--border)", borderRadius: 6,
                                                                padding: "4px 8px", cursor: "pointer", fontSize: 11,
                                                                color: copiedId === c.id ? "#22c55e" : "var(--ink-500)",
                                                                display: "flex", alignItems: "center", gap: 4, whiteSpace: "nowrap",
                                                            }}
                                                            title={c.linkToSend}
                                                        >
                                                            <LinkIcon size={12} />
                                                            {copiedId === c.id ? "Copied" : "Copy Link"}
                                                        </button>
                                                    )}
                                                    {c.showcaseKey && (
                                                        <a
                                                            href={"/showcase/" + c.showcaseKey}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            style={{
                                                                color: "#5b8fa8", display: "flex", alignItems: "center",
                                                                padding: 4,
                                                            }}
                                                            title="Open showcase"
                                                        >
                                                            <ExternalLink size={14} />
                                                        </a>
                                                    )}
                                                    {(c.effectiveStatus || c.status) !== "SUBMITTED" && (
                                                        <button
                                                            onClick={() => handleDelete(c.id)}
                                                            disabled={deletingId === c.id}
                                                            title="Delete candidate (also removes manuscript)"
                                                            style={{
                                                                background: "none", border: "none", cursor: deletingId === c.id ? "default" : "pointer",
                                                                color: "var(--ink-200)", padding: 4, display: "flex", alignItems: "center",
                                                                opacity: deletingId === c.id ? 0.5 : 1,
                                                            }}
                                                        >
                                                            <Trash2 size={14} />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            </>
                        );
                        })()}
                    </div>
        </AdminLayout>
    );
}
